/**
 * The concrete IndexedDB schema fixed by D-027.
 *
 * This is expressed as data rather than as imperative calls so that the
 * migrations, the tests, and the schema-agnostic recovery mode can all read the
 * same definition. Every index below is justified by a real workflow — there
 * are no speculative indexes (docs/architecture.md).
 *
 * Each version's definition is frozen once released, because a device in the
 * field has already run the migration built from it. `CURRENT_SCHEMA` at the
 * foot of this file composes them into what this build expects.
 *
 * "Frozen" covers `name`, `keyPath` and `indexes` — the three fields a migration
 * actually reads. `keyRationale` is prose that no migration touches, so it may be
 * corrected or extended afterwards, and doing so cannot change what any device
 * has already created.
 */

export const STORE = {
  profile: 'profile',
  settings: 'settings',
  projects: 'projects',
  goals: 'goals',
  phases: 'phases',
  weightEntries: 'weightEntries',
  measurementSessions: 'measurementSessions',
  photos: 'photos',
  sentinelLifts: 'sentinelLifts',
  strengthEntries: 'strengthEntries',
  weeklyReviews: 'weeklyReviews',
  opinions: 'opinions',
  attachments: 'attachments',
  blobs: 'blobs',
  meta: 'meta',
  /* Version 2, schema v3 (D-090, D-092). */
  domainGoals: 'domainGoals',
  nutritionPlans: 'nutritionPlans',
  dailyChecks: 'dailyChecks',
  routineAnchors: 'routineAnchors',
  wearableImports: 'wearableImports',
  aiProposals: 'aiProposals',
  connections: 'connections',
  /* Version 2, schema v4 (D-105). */
  trainingWeeks: 'trainingWeeks',
} as const

export type StoreName = (typeof STORE)[keyof typeof STORE]

export interface IndexDefinition {
  readonly name: string
  readonly keyPath: string | readonly string[]
  readonly unique?: boolean
}

export interface StoreDefinition {
  readonly name: StoreName
  readonly keyPath: string
  readonly indexes: readonly IndexDefinition[]
  /** Why this store is keyed the way it is. Kept with the definition so the
      reasoning survives longer than any single reviewer's memory. */
  readonly keyRationale: string
}

export const SCHEMA_V1: readonly StoreDefinition[] = [
  {
    name: STORE.profile,
    keyPath: 'id',
    indexes: [],
    keyRationale:
      "Singleton keyed 'singleton' — Version 1 is single-user (D-013), so multiple profiles are unrepresentable.",
  },
  {
    name: STORE.settings,
    keyPath: 'id',
    indexes: [],
    keyRationale: "Singleton keyed 'singleton', as above.",
  },
  {
    name: STORE.goals,
    keyPath: 'id',
    indexes: [
      { name: 'status', keyPath: 'status' },
      { name: 'targetDate', keyPath: 'targetDate' },
    ],
    keyRationale: 'Opaque id; status and targetDate drive the active-goal lookup and projections.',
  },
  {
    name: STORE.phases,
    keyPath: 'id',
    indexes: [
      { name: 'startDate', keyPath: 'startDate' },
      { name: 'status', keyPath: 'status' },
      { name: 'type', keyPath: 'type' },
    ],
    keyRationale:
      'Opaque id; startDate orders phase history, status finds the active phase, type supports phase-boundary rendering.',
  },
  {
    name: STORE.weightEntries,
    keyPath: 'date',
    indexes: [{ name: 'phaseId', keyPath: 'phaseId' }],
    keyRationale:
      'Natural key: the calendar date (D-027, D-035). One entry per day becomes structural rather than a validation rule, and the trailing seven-day window is a single bounded IDBKeyRange scan.',
  },
  {
    name: STORE.measurementSessions,
    keyPath: 'id',
    indexes: [
      { name: 'date', keyPath: 'date' },
      { name: 'reviewId', keyPath: 'reviewId' },
      { name: 'phaseId', keyPath: 'phaseId' },
    ],
    keyRationale:
      'Opaque id; date orders the trend, reviewId links a session to its weekly review.',
  },
  {
    name: STORE.photos,
    keyPath: 'id',
    indexes: [
      { name: 'capturedDate', keyPath: 'capturedDate' },
      { name: 'view', keyPath: 'view' },
      { name: 'reviewId', keyPath: 'reviewId' },
      { name: 'phaseId', keyPath: 'phaseId' },
    ],
    keyRationale:
      'Opaque id; view serves front/side/back timelines, reviewId serves a review photo set. Bytes live in `blobs`, so listing reads metadata only.',
  },
  {
    name: STORE.sentinelLifts,
    keyPath: 'id',
    indexes: [
      { name: 'active', keyPath: 'active' },
      { name: 'order', keyPath: 'order' },
    ],
    keyRationale:
      'Opaque id; active and order drive the configured lift list. `active` holds a STRING, not a boolean — IndexedDB has no boolean key type and a record holding `true` at an indexed property is silently left out of the index rather than rejected, so the active-lift list would be empty forever with no error anywhere (D-072). The v1 schema declared this index before the value type was settled, which is how the hazard came to be latent here.',
  },
  {
    name: STORE.strengthEntries,
    keyPath: 'id',
    indexes: [
      { name: 'liftId', keyPath: 'liftId' },
      { name: 'date', keyPath: 'date' },
      { name: 'liftId_date', keyPath: ['liftId', 'date'] },
    ],
    keyRationale:
      'Opaque id; the compound [liftId, date] index serves the previous-week and four-week comparisons directly.',
  },
  {
    name: STORE.weeklyReviews,
    keyPath: 'id',
    indexes: [
      { name: 'weekStartDate', keyPath: 'weekStartDate' },
      { name: 'status', keyPath: 'status' },
      { name: 'phaseId', keyPath: 'phaseId' },
    ],
    keyRationale:
      'Opaque id. phaseId is a real foreign key for grouping and phase-boundary rendering, and is distinct from the immutable phaseSnapshot held on the record (D-046).',
  },
  {
    name: STORE.opinions,
    keyPath: 'id',
    indexes: [
      { name: 'reviewId', keyPath: 'reviewId' },
      { name: 'generatedAt', keyPath: 'generatedAt' },
    ],
    keyRationale: 'Opaque id; reviewId retrieves the opinion attached to a finalized review.',
  },
  {
    name: STORE.attachments,
    keyPath: 'id',
    indexes: [
      { name: 'kind', keyPath: 'kind' },
      { name: 'relatedId', keyPath: 'relatedId' },
    ],
    keyRationale: 'Opaque id; relatedId resolves an attachment back to its owning record.',
  },
  {
    name: STORE.blobs,
    keyPath: 'blobId',
    indexes: [],
    keyRationale:
      'No index by design: a pure content store addressed only by id from an owning record. Payloads are immutable once written (D-029).',
  },
  {
    name: STORE.meta,
    keyPath: 'key',
    indexes: [],
    keyRationale:
      'Operational state, not user preferences: lastBackupAt, migrationState, restoreState, recoverySnapshotRef, installedAt, tagVocabularyVersion (D-046).',
  },
]

/**
 * Version 2 — the `projects` store and its three foreign keys (D-062).
 *
 * **This is the first schema change since the initial one.** All fourteen v1
 * stores were created up front precisely so that adding one later would be
 * rare; `projects` is the case D-062 decided was worth it, because a project
 * needs somewhere to keep a recommendation snapshot and a completion summary
 * and neither fits on a phase or a goal.
 *
 * `SCHEMA_V1` above is **frozen**: it is what migration v1 creates, and a
 * device in the field has already run it. The additions live here and the two
 * are composed into `CURRENT_SCHEMA` below.
 */
export const PROJECTS_STORE: StoreDefinition = {
  name: STORE.projects,
  keyPath: 'id',
  indexes: [{ name: 'status', keyPath: 'status' }],
  keyRationale:
    'Opaque id; status finds the active project, which is the only lookup the app performs. There is no date index: project history is short enough to sort in memory, and a speculative index is exactly what D-027 forbids.',
}

/**
 * `projectId` on the three stores a project owns (D-062).
 *
 * A project spans one or more phases, so the foreign key goes on the phase
 * rather than a phase list going on the project. Goals and weekly reviews carry
 * it for the same reason: grouping them by project must not require walking
 * phases to find out which project a phase belonged to.
 */
export const SCHEMA_V2_ADDED_INDEXES: Readonly<Record<string, readonly IndexDefinition[]>> = {
  [STORE.phases]: [{ name: 'projectId', keyPath: 'projectId' }],
  [STORE.goals]: [{ name: 'projectId', keyPath: 'projectId' }],
  [STORE.weeklyReviews]: [{ name: 'projectId', keyPath: 'projectId' }],
}

/**
 * Version 3 — the seven stores the opt-in domain model needs (D-090, D-092).
 *
 * **Additive only.** No existing store, index or record changes, which is what
 * makes this migration ordinary rather than the exceptional one v2 was.
 *
 * Three of the seven are modelled at C6.0 and four are not: `routineAnchors`,
 * `wearableImports`, `aiProposals` and `connections` are created here and typed
 * as `UnmodelledRecord` until their own checkpoints. That is the v1 precedent
 * applied deliberately — creating a store later is the expensive operation
 * (D-027), and the four are already known to be needed, so creating them now
 * means C6.5, C7.1 and C8.0 need no migration of their own.
 *
 * `SCHEMA_V1` and the v2 additions above are **frozen**: a device in the field
 * has already run both.
 */
export const SCHEMA_V3_STORES: readonly StoreDefinition[] = [
  {
    name: STORE.domainGoals,
    keyPath: 'id',
    indexes: [
      { name: 'projectId', keyPath: 'projectId' },
      { name: 'domain', keyPath: 'domain' },
      { name: 'status', keyPath: 'status' },
    ],
    keyRationale:
      'Opaque id. projectId groups a project\'s domains, domain answers "is cardio active?" without a scan, and status finds the participating set — the three questions every adaptive screen asks. There is no compound [projectId, domain] index: one project holds at most four domain goals, so the narrowing a compound index would buy is a filter over four records.',
  },
  {
    name: STORE.nutritionPlans,
    keyPath: 'id',
    indexes: [
      { name: 'projectId', keyPath: 'projectId' },
      { name: 'effectiveFrom', keyPath: 'effectiveFrom' },
    ],
    keyRationale:
      'Opaque id; effectiveFrom orders the supersession chain, which is the only way this store is read. A "current plan" index is deliberately absent — currency is the absence of effectiveTo, and IndexedDB cannot index the absence of a field.',
  },
  {
    name: STORE.dailyChecks,
    keyPath: 'date',
    indexes: [{ name: 'projectId', keyPath: 'projectId' }],
    keyRationale:
      'Natural key: the calendar date, following weightEntries (D-027, D-035). One answer per day becomes structural rather than a validation rule, correcting a day is an overwrite, and the trailing-week read is a single bounded IDBKeyRange scan.',
  },
  {
    name: STORE.routineAnchors,
    keyPath: 'id',
    indexes: [
      { name: 'kind', keyPath: 'kind' },
      { name: 'enabled', keyPath: 'enabled' },
    ],
    keyRationale:
      'Opaque id; kind resolves an anchor to the task it belongs to. `enabled` is a STRING, not a boolean — IndexedDB has no boolean key type and a record holding `true` at an indexed property is silently left out of the index rather than rejected, which is the latent defect the v1 schema shipped on sentinelLifts.active.',
  },
  {
    name: STORE.wearableImports,
    keyPath: 'id',
    indexes: [
      { name: 'date', keyPath: 'date' },
      { name: 'category', keyPath: 'category' },
      { name: 'date_category', keyPath: ['date', 'category'] },
    ],
    keyRationale:
      'Opaque id; the compound [date, category] index serves "what did the wearable say about sleep that day?" directly. Deliberately NOT keyed by date — a day carries several categories, and a supersession keeps both the import and the manual override, so several records legitimately share a date.',
  },
  {
    name: STORE.aiProposals,
    keyPath: 'id',
    indexes: [{ name: 'requestedAt', keyPath: 'requestedAt' }],
    keyRationale:
      'Opaque id; requestedAt orders the history, which is the only lookup. A proposal is immutable once written, so there is nothing to find it by other than when it was asked for.',
  },
  {
    name: STORE.connections,
    keyPath: 'provider',
    indexes: [],
    keyRationale:
      'Natural key: the provider name. One connection per provider is structural, so a second Fitbit connection is unrepresentable rather than merely invalid. No index — the store holds a handful of records read by key, and it is the only store excluded from backup and export, so nothing sweeps it.',
  },
]

/**
 * Version 4 — the materialised training week (D-105 §12).
 *
 * **Additive only, and there is no backfill.** An existing project gains no
 * schedule and no historical weeks retroactively: a week the user never saw must
 * not be manufactured so that it can be reported as empty (D-105 §6). The
 * schedule itself needs no store — it rides inside `domainGoals` as an optional
 * field on records that are not rewritten.
 *
 * `SCHEMA_V1`, the v2 additions and `SCHEMA_V3_STORES` are all **frozen**: a
 * device in the field has run every one of them.
 */
export const SCHEMA_V4_STORES: readonly StoreDefinition[] = [
  {
    name: STORE.trainingWeeks,
    keyPath: 'id',
    indexes: [
      { name: 'weekStartDate', keyPath: 'weekStartDate' },
      { name: 'projectId', keyPath: 'projectId' },
    ],
    keyRationale:
      "Opaque id, following weeklyReviews rather than inventing a scheme. Deliberately NOT keyed by weekStartDate: a mid-project change of weekStartDay (D-105 §8) and a project started mid-week each produce two records claiming the same day, so the natural key would collide exactly where the data matters most. weekStartDate finds the current week and projectId groups a project's history — the only two lookups there are.",
  },
]

/**
 * The schema this build expects, composed from v1 plus the v2 and v3 additions.
 *
 * Read this — never `SCHEMA_V1` — wherever the question is "what stores and
 * indexes should exist now?". Three places ask exactly that: the full clear
 * (`clearAll.ts`), backup validation's known-store check, and the schema test.
 * Leaving them pointed at `SCHEMA_V1` would have silently excluded `projects`
 * from a reset and from every backup integrity check.
 */
export const CURRENT_SCHEMA: readonly StoreDefinition[] = [
  ...SCHEMA_V1.map((definition) => {
    const added = SCHEMA_V2_ADDED_INDEXES[definition.name]
    return added === undefined
      ? definition
      : { ...definition, indexes: [...definition.indexes, ...added] }
  }),
  PROJECTS_STORE,
  ...SCHEMA_V3_STORES,
  ...SCHEMA_V4_STORES,
]

/** Keys used in the `meta` store. */
export const META_KEY = {
  lastBackupAt: 'lastBackupAt',
  migrationState: 'migrationState',
  restoreState: 'restoreState',
  installedAt: 'installedAt',
  tagVocabularyVersion: 'tagVocabularyVersion',
  /* The Project Setup wizard's draft (D-062). Operational state rather than a
     user preference, and deliberately not its own store: it exists only between
     the first step and the confirmation, and the transaction that commits the
     setup removes it. */
  projectSetupDraft: 'projectSetupDraft',
  /* When the user last dismissed the backup reminder (D-084). Operational
     state, so it belongs beside `lastBackupAt` in `meta` rather than in user
     settings — the user is not choosing a preference, they are saying "not
     now". Honoured for seven days, never forever. */
  backupReminderDismissedAt: 'backupReminderDismissedAt',
} as const

export type MetaKey = (typeof META_KEY)[keyof typeof META_KEY]
